import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Controller, useForm } from 'react-hook-form';
import { useSelector, useDispatch } from 'react-redux';
import Moment from 'moment';

import { Input } from '@/components/ui/inputs/input';
import {
  forgetPasswordAuthService,
  getGeneralSettingStoreAfterLoginService,
} from '@/services/AuthServices';
import validationErrors from '@/lib/validationErrors';
import { GeneralSettingList } from '@/constants/GeneralSettingConstant';

import backgroundImage from '@/assets/images/image1.jpg';
import logoImage from '@/assets/images/logo.png';

const BRAND = { primary: '#156372', yellow: '#FFC107', ink: '#0F2F35' };

export default function ForgotPasswordPage() {
  const { isAuthenticated, userData } = useSelector((s) => s?.Auth || {});
  const { todayDateUpdate } = useSelector((s) => s?.GeneralSetting || {});
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { control, handleSubmit, setValue, reset, formState: { errors } } = useForm({ defaultValues: { email: '' } });
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (isAuthenticated && userData?.id) {
      navigate('/admin/dashboard');
      return;
    }
    // clear any stale verification token; keep email so we can pre-fill if needed
    try { localStorage.removeItem('fp_token'); } catch {}

    setValue('email', '');
    if (todayDateUpdate !== Moment.utc().format('DD/MM/YYYY')) {
      dispatch(getGeneralSettingStoreAfterLoginService({ key_list: GeneralSettingList })).catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onSubmit = (data) => {
    const formEmail = (data?.email || '').trim();
    if (!formEmail) return;

    setIsLoading(true);
    dispatch(forgetPasswordAuthService({ email: formEmail }))
      .then(() => {
        try { localStorage.setItem('fp_email', formEmail); } catch {}
        reset({ email: '' });
        navigate(`/forget-password/verify?email=${encodeURIComponent(formEmail)}`, {
          state: { email: formEmail },
          replace: true,
        });
      })
      .catch(({ message, errorData } = {}) => {
        validationErrors(errorData, message);
      })
      .finally(() => setIsLoading(false));
  };

  return (
    <section className="min-h-screen flex items-center" style={{ backgroundColor: BRAND.yellow }}>
      <div className="mx-auto max-w-7xl px-4 py-10 lg:py-16 w-full">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-10 items-center">
          {/* LEFT — Form card */}
          <div className="lg:col-span-5 order-2 lg:order-1">
            <div className="relative z-10 lg:-mr-12">
              <div
                className="relative rounded-3xl shadow-2xl ring-1 ring-black/5 overflow-hidden"
                style={{ backgroundColor: BRAND.primary }}
              >
                <div className="absolute inset-y-0 left-0 w-1" style={{ backgroundColor: BRAND.yellow }} />
                <div className="p-6 md:p-10">
                  <div className="flex flex-col items-center mb-6">
                    <img src={logoImage} alt="Company logo" className="h-16 w-auto mb-3" />
                    <h1 className="text-3xl md:text-4xl font-semibold text-center tracking-tight text-white">
                      Forgot Password
                    </h1>
                    <p className="mt-2 text-sm text-white/80 text-center">
                      Enter your email to receive a verification code
                    </p>
                  </div>

                  {Object.keys(errors || {}).length > 0 && (
                    <div className="mb-3 rounded-md bg-red-50 text-red-700 text-sm px-3 py-2">
                      Please fix the highlighted fields below.
                    </div>
                  )}

                  <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
                    <Controller
                      control={control}
                      name="email"
                      rules={{ required: true, pattern: /^[^@ ]+@[^@ ]+\.[^@ .]{2,}$/ }}
                      render={({ field }) => (
                        <Input
                          {...field}
                          autoFocus
                          type="email"
                          labelText="Email"
                          placeholder="Enter your email"
                          errorType={errors?.email?.type}
                          groupClassName="mb-1"
                        />
                      )}
                    />

                    <button
                      type="submit"
                      disabled={isLoading}
                      className="w-full py-3 rounded-xl font-semibold shadow focus:outline-none focus:ring-2 hover:brightness-95 active:brightness-90 disabled:opacity-80 disabled:cursor-not-allowed"
                      style={{ backgroundColor: BRAND.yellow, color: BRAND.ink, boxShadow: '0 12px 30px rgba(0,0,0,.25)' }}
                    >
                      {isLoading ? 'Sending…' : 'Next'}
                    </button>

                    <div className="flex items-center justify-center gap-2 pt-1">
                      <span className="text-sm text-white/80">Remember your password?</span>
                      <Link className="text-sm font-medium hover:underline" to="/sign-in" style={{ color: BRAND.yellow }}>
                        Log in
                      </Link>
                    </div>

                    <div className="pt-4 text-center text-[11px] text-white/80">Secure • Code sent to your email</div>
                  </form>
                </div>
              </div>
            </div>
          </div>

          {/* RIGHT — Image panel */}
          <div className="lg:col-span-7 order-1 lg:order-2">
            <div
              className="relative h-[44vh] sm:h-[50vh] md:h-[58vh] lg:h-[78vh] rounded-3xl overflow-hidden shadow-2xl ring-1 ring-black/5"
              style={{ clipPath: 'polygon(7% 0%, 100% 0%, 100% 100%, 0% 100%)' }}
            >
              <img src={backgroundImage} alt="Security visuals" className="absolute inset-0 h-full w-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-l from-black/15 via-transparent to-transparent" />
              <div className="absolute top-0 right-0 h-1 w-1/2" style={{ backgroundColor: BRAND.primary }} />
              <div className="hidden md:flex absolute bottom-4 right-4">
                <span className="px-4 py-2 rounded-full text-xs font-semibold shadow-md ring-1 ring-black/5 bg-white/90 text-gray-700 backdrop-blur">
                  Password Recovery • Secure
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
